// pages/AppLogin.jsx
/**
 * AppLogin (알림 신청 페이지 전용 로그인)
 * --------------------------------------------------------------------
 * 목적:
 *  - 사업자번호/비밀번호로 로그인하여 accessToken과 상호작용 쿠키(리프레시) 기반 세션을 구성.
 *  - 로그인 성공 시 세션스토리지에 token/name 저장 후 /application 으로 이동.
 *  - 진입 시 쿠키 기반 refresh 토큰으로 자동 로그인 시도(있으면 바로 통과).
 *
 * 인증 흐름:
 *  - /api/auth/login POST { id, password, remember_me }
 *    → { token, name } 수신 → 세션스토리지 저장 → mokiApi Authorization 헤더 설정.
 *  - 마운트 시 /api/auth/refresh POST (withCredentials: true)
 *    → { token, name } 수신 시 동일하게 세팅 후 바로 handleLoginSuccess().
 *
 */

import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux"; // 현재는 미사용, 추후 상태 저장 시 활용 가능
import { useNavigate } from "react-router-dom";
import { mokiApi } from "../../services/loginApi";
import axios from "axios";
import ToastMessage from "../../component/common/Toast";
import { BASE_URL } from "../../component/Url";

function AppLogin() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // 폼 상태
  const [inputValue, setInputValue] = useState({ id: "", pswd: "" });
  // 에러 토스트
  const [toast, setToast] = useState(false);
  // 자동 로그인(서버 remember_me 정책과 연동)
  const [autoLogin, setAutoLogin] = useState(false);

  // 입력 핸들러: name 키(id/pswd)에 따라 값 업데이트
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputValue((prev) => ({ ...prev, [name]: value }));
  };

  // 로그인 성공 후 이동(세션 세팅은 호출측에서 처리)
  const handleLoginSuccess = () => {
    navigate("../application");
  };

  // 로그인 처리
  const handleLogin = async (e) => {
    // axios 전역 Authorization 제거: 외부 인터셉터/잔여 헤더 영향 차단
    delete axios.defaults.headers.common["Authorization"];

    try {
      // 서버 계약: { id, password, remember_me } → { token, name }
      const { data, status } = await mokiApi.post("/api/auth/login", {
        id: inputValue.id,
        password: inputValue.pswd,
        remember_me: autoLogin,
      });

      // 세션 스토리지에 토큰/이름 저장
      sessionStorage.setItem("accessToken", data.token);
      sessionStorage.setItem("name", data.name);

      // 이후 요청을 위한 Authorization 설정
      mokiApi.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;

      if (status === 200) handleLoginSuccess();
    } catch {
      // 실패: 에러 토스트 노출
      setToast(true);
    }
  };

  // 마운트 시 리프레시 토큰으로 자동 로그인 시도
  useEffect(() => {
    const refreshToken = async () => {
      try {
        // 쿠키 포함 요청
        const res = await axios.post(
          `${BASE_URL}api/auth/refresh`,
          {},
          { withCredentials: true }
        );
        if (res.status === 200) {
          const { token, name } = res.data;
          // 세션 업데이트 + Authorization 갱신
          sessionStorage.setItem("accessToken", token);
          sessionStorage.setItem("name", name);
          mokiApi.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          // 즉시 페이지 이동
          handleLoginSuccess();
        }
      } catch {
        // 리프레시 실패는 무시(수동 로그인 유도)
      }
    };
    refreshToken();
  }, []);

  // UI: 단순 로그인 폼
  return (
    <div className="flex flex-col w-full max-w-[80vw] h-screen items-center mx-auto">
      {/* 로고/타이틀 */}
      <div className="flex flex-col items-center mt-[20vh] ">
        <img src="/assets/report.png" alt="Moki Logo" className="w-[13vw]" />
        <div className="flex flex-col items-center justify-center text-[vw] m-[2vh] font-extrabold">
          <div>실시간 매출분석 데이터</div>
          <div>카카오톡 알림신청</div>
        </div>
      </div>

      {/* 입력 폼 */}
      <div className="w-[90%]">
        <label
          htmlFor="id"
          className="block mt-[5vw] text-[3.3vw] font-medium text-gray-700"
        >
          아이디(사업자번호)
        </label>
        <input
          id="id"
          name="id"
          value={inputValue.id}
          onChange={handleInputChange}
          className="w-full h-[35px] text-lg border-b-2 border-gray-300 focus:outline-none"
        />

        <label
          htmlFor="pswd"
          className="block mt-[5vw] text-[3.3vw] font-medium text-gray-700"
        >
          비밀번호
        </label>
        <input
          id="pswd"
          name="pswd"
          type="password"
          value={inputValue.pswd}
          onChange={handleInputChange}
          className="w-full h-[35px] text-lg border-b-2 border-gray-300 focus:outline-none mt-[1vw]"
        />
      </div>

      {/* 로그인 버튼: 값이 모두 있을 때만 활성화 */}
      <div className="w-[90%] mt-[18vw]">
        <button
          onClick={handleLogin}
          disabled={!(inputValue.id.trim() && inputValue.pswd.trim())}
          className={`w-full h-[10vw] text-[3.5vw] rounded-md transition-colors
            ${
              inputValue.id.trim() && inputValue.pswd.trim()
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
        >
          로그인
        </button>
      </div>

      {/* 에러 토스트 */}
      {toast && (
        <ToastMessage
          setToast={setToast}
          text="아이디 또는 비밀번호가 일치하지 않습니다."
        />
      )}
    </div>
  );
}

export default AppLogin;
